import { type ReactNode, Children, isValidElement } from 'react';

import { Accordion, AccordionItem, AccordionButton, AccordionPanel, Icon } from '@lib/ui';
import { cn, cva, setThisClass } from '@lib/utils';
import { useSidebarMenu } from './SidebarMenu.context';

/**
 * Check if any immediate child has the given activeId
 */
function containsActiveChild(children: ReactNode, activeId: string | undefined): boolean {
  if (!activeId) return false;

  let found = false;
  Children.forEach(children, (child) => {
    if (isValidElement<{ id?: string }>(child) && child.props.id === activeId) {
      found = true;
    }
  });
  return found;
}

const sidebarNavCollapsibleButtonVariants = cva(
  [
    // Layout
    'group flex min-w-0 flex-1 items-center justify-between rounded-lg px-3 py-2',
    // Typography
    'font-medium',
    // Cursor
    'cursor-pointer',
    // Transitions
    'outline-hidden transition-colors duration-300 ease-in-out',
  ],
  {
    variants: {
      open: {
        true: [
          'text-fg-muted',
          // Hover
          'hover:bg-ui-hover hover:text-fg',
          // Focus
          'focus:bg-ui-hover focus:text-fg',
        ],
        false: [
          'text-fg-muted',
          // Hover
          'hover:bg-ui-hover hover:text-fg',
          // Focus
          'focus:bg-ui-hover focus:text-fg',
        ],
      },
    },
    defaultVariants: {
      open: false,
    },
  }
);

const iconVariants = cva('shrink-0 [&>svg]:size-5 [&>svg]:stroke-[1.5]', {
  variants: {
    open: {
      true: '',
      false: 'opacity-80 group-hover:opacity-100',
    },
  },
  defaultVariants: {
    open: false,
  },
});

const chevronVariants = cva(['size-4 shrink-0 transition-transform rtl:-scale-x-100'], {
  variants: {
    open: {
      true: 'rotate-90',
      false: '',
    },
  },
  defaultVariants: {
    open: false,
  },
});

export interface SidebarNavCollapsibleProps {
  /** Unique identifier for this group */
  id: string;
  /** Icon element */
  icon: ReactNode;
  /** Group label */
  label: string;
  /** Child nav items */
  children: ReactNode;
  /** Whether the group should be expanded by default */
  defaultOpen?: boolean;
  /** Whether to auto-expand when a child item is active */
  autoExpandOnActive?: boolean;
}

export function SidebarNavCollapsible({
  id,
  icon,
  label,
  children,
  defaultOpen = false,
  autoExpandOnActive = true,
}: SidebarNavCollapsibleProps) {
  const { activeId } = useSidebarMenu();

  // Auto-expand if any child is active
  const hasActiveChild = autoExpandOnActive && containsActiveChild(children, activeId);
  const shouldBeOpen = defaultOpen || hasActiveChild;

  return (
    <Accordion
      defaultValue={shouldBeOpen ? id : undefined}
      key={hasActiveChild ? 'active' : 'default'}
    >
      <AccordionItem value={id} className="relative flex flex-1 flex-col px-3">
        {({ open }) => (
          <>
            <AccordionButton
              icon={false}
              className={cn(setThisClass('primary'), sidebarNavCollapsibleButtonVariants({ open }))}
            >
              <div className="flex min-w-0 items-center gap-3">
                <span className={iconVariants({ open })}>{icon}</span>
                <span className="truncate">{label}</span>
              </div>
              <Icon name="chevron-right" className={chevronVariants({ open })} />
            </AccordionButton>
            <AccordionPanel className="flex flex-col space-y-1 px-3 py-1.5">
              {children}
            </AccordionPanel>
          </>
        )}
      </AccordionItem>
    </Accordion>
  );
}

SidebarNavCollapsible.displayName = 'SidebarNavCollapsible';
